/**
 * components/viewer/PhotoTimeline.jsx — Iter 35.
 *
 * Inline photo strip rendered inside the Inspector for any object that
 * supports the (origin_type, origin_id) timeline contract — today
 * structures + revisions + pins + measurements + free_location.
 *
 * Behavior contract:
 *   - Newest first.
 *   - No folders. No albums. No categories.
 *   - Tap a thumb → lightbox (full image + restrained caption).
 *   - Capture button is always present and ALWAYS works on first tap.
 *
 * Drawing remains the hero — this strip lives inside the Inspector and
 * never blocks the canvas.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Camera, X, Trash2, Play } from "lucide-react";
import { api, photoThumbUrl, photoFileUrl } from "@/lib/api";
import { enqueue, listByScope, onChange as onSyncChange, drainNow, remove as removeOp } from "@/lib/syncQueue";


/**
 * Props:
 *   originType  — "structure" | "pin" | "revision" | "measurement" | "free_location"
 *   originId    — object PK; null only for `free_location`
 *   originLabel — cached human label ("SMH-25") for the audit row + capture UX
 *   sheetId     — required for upload + spatial defaults
 *   normalizedX / normalizedY — capture coords (use the object's location)
 *   gpsLat / gpsLon / gpsAccuracy — passed through if available
 */
export default function PhotoTimeline({
  originType,
  originId,
  originLabel,
  sheetId,
  normalizedX,
  normalizedY,
  gpsLat,
  gpsLon,
  gpsAccuracy,
}) {
  const [photos, setPhotos] = useState([]);
  const [pendingOps, setPendingOps] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [lightbox, setLightbox] = useState(null); // photo doc
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Iter 36 — scope_key lets the queue surface only ops for THIS object
  // when we render the optimistic pending photos alongside server rows.
  const scopeKey = useMemo(
    () => (originType && originId ? `photos:${originType}:${originId}` : null),
    [originType, originId],
  );

  // Object timeline endpoint; free_location uses the sheet-scope endpoint
  // because there's no FK to query on (every free_location capture is
  // independent and not aggregated by object).
  const refetch = async () => {
    try {
      let rows = [];
      if (originType && originId) {
        const { data } = await api.get(`/objects/${originType}/${originId}/photos`);
        rows = data || [];
      }
      setPhotos(rows);
    } catch {
      // Offline / network error — keep whatever rows we last saw (the
      // service worker may serve a cached payload via stale-while-revalidate).
    } finally {
      setLoaded(true);
    }
  };
  // Refresh the pending-ops list for this object (optimistic UI).
  const refreshPending = async () => {
    if (!scopeKey) { setPendingOps([]); return; }
    setPendingOps(await listByScope(scopeKey));
  };
  useEffect(() => {
    if (originType && originId) { refetch(); refreshPending(); }
    else { setPhotos([]); setPendingOps([]); setLoaded(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originType, originId]);

  // Refresh after a successful drain (queue emits change events).
  useEffect(() => {
    const off = onSyncChange(() => { refreshPending(); refetch(); });
    return off;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originType, originId]);

  // ----- capture -----
  const onCaptureClick = () => {
    if (uploading || !sheetId) return;
    fileInputRef.current?.click();
  };

  /**
   * Opportunistic GPS — independent of the global Locate Me toggle.
   * Per the Iter 35 directive, photos auto-inherit GPS "when available"
   * which means a one-shot fix at capture time, not "user must have
   * toggled Follow Mode first". Falls through gracefully if denied /
   * unsupported / times out.
   */
  const _currentGpsFix = () => new Promise((resolve) => {
    // Prefer the prop-supplied live fix (already-running watchPosition
    // from SheetViewer's Locate stack) for zero-latency capture.
    if (gpsLat != null && gpsLon != null) {
      resolve({ lat: gpsLat, lon: gpsLon, accuracy: gpsAccuracy ?? null });
      return;
    }
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve(null); return;
    }
    let done = false;
    const finish = (v) => { if (!done) { done = true; resolve(v); } };
    // 4s timeout — capture must NEVER block on slow GPS. Better to save
    // without GPS than to leave the super staring at a spinner.
    const tid = setTimeout(() => finish(null), 4000);
    navigator.geolocation.getCurrentPosition(
      (p) => { clearTimeout(tid); finish({ lat: p.coords.latitude, lon: p.coords.longitude, accuracy: p.coords.accuracy }); },
      () => { clearTimeout(tid); finish(null); },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 4000 },
    );
  });

  // Wave 8 (2026-09-11) — best-effort in-browser video duration probe.
  // Loads just the metadata (no full decode/download), 3s cap, resolves
  // null on any failure/timeout rather than blocking the capture.
  const _probeVideoDuration = (file) => new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const v = document.createElement("video");
      let done = false;
      const finish = (val) => {
        if (done) return;
        done = true;
        URL.revokeObjectURL(url);
        resolve(val);
      };
      const tid = setTimeout(() => finish(null), 3000);
      v.preload = "metadata";
      v.onloadedmetadata = () => { clearTimeout(tid); finish(Number.isFinite(v.duration) ? v.duration : null); };
      v.onerror = () => { clearTimeout(tid); finish(null); };
      v.src = url;
    } catch {
      resolve(null);
    }
  });

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset for next capture
    if (!file) return;
    setUploading(true);
    try {
      // Founder Continuation (2026-09-08) — generate ONE stable id for this
      // capture up front, before the first network attempt. If the direct
      // attempt below actually succeeds server-side but the client never
      // sees the response (timeout, dropped connection) and falls through
      // to the offline queue, the queued retry reuses this SAME id — the
      // server recognizes it as a retry of the same logical photo instead
      // of creating (and billing storage for) a second one. A genuinely
      // new capture always calls this function again and gets a new id.
      const clientOpId = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `capture_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      // Resolve GPS opportunistically — never block the upload more than 4s.
      const fix = await _currentGpsFix();
      const formFields = {
        normalized_x: normalizedX ?? 0.5,
        normalized_y: normalizedY ?? 0.5,
        origin_type: originType,
      };
      if (originId) formFields.origin_id = originId;
      if (fix && fix.lat != null && fix.lon != null) {
        formFields.gps_lat = fix.lat;
        formFields.gps_lon = fix.lon;
        if (fix.accuracy != null) formFields.gps_accuracy_m = fix.accuracy;
      }
      // Wave 8 (2026-09-11) — best-effort client-side duration probe for
      // video captures. Never blocks the upload: a 3s cap, and any
      // failure just leaves duration_seconds unset (server tolerates None).
      if ((file.type || "").startsWith("video/")) {
        const dur = await _probeVideoDuration(file);
        if (dur != null) formFields.duration_seconds = dur;
      }
      // Iter 36 — trust-first upload. Try the network with a short
      // timeout; on ANY failure (offline, transient 5xx, slow), enqueue
      // the capture to IDB and let the queue drain when connectivity
      // returns. The user sees the photo immediately either way.
      try {
        const form = new FormData();
        Object.entries(formFields).forEach(([k, v]) => form.append(k, String(v)));
        form.append("client_op_id", clientOpId);
        form.append("file", file);
        await api.post(`/sheets/${sheetId}/photos`, form, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 8000,
        });
        await refetch();
      } catch (netErr) {
        // Queue + optimistic render.
        await enqueue({
          id: clientOpId,
          op_type: "photo.capture",
          endpoint: `/sheets/${sheetId}/photos`,
          method: "POST",
          form_fields: formFields,
          blob: file,
          filename: file.name,
          content_type: file.type || "image/jpeg",
          scope_key: scopeKey,
        });
        await refreshPending();
        // Probe in case the failure was transient.
        drainNow();
      }
    } catch (err) {
      console.error("photo upload failed", err);
    } finally {
      setUploading(false);
    }
  };

  // ----- delete -----
  const onDelete = async (photoId) => {
    try {
      await api.delete(`/photos/${photoId}`);
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));
      if (lightbox?.id === photoId) setLightbox(null);
    } catch {/* silent — UI stays consistent on next refetch */}
  };

  const totalCount = photos.length + pendingOps.length;
  const showStrip = loaded && (totalCount > 0 || originId);
  if (!showStrip) return null;

  return (
    <div className="border-t border-[var(--fop-border)] px-4 py-3" data-testid="photo-timeline" data-count={totalCount} data-pending-count={pendingOps.length}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">
          Photos & Video · {totalCount}{pendingOps.length > 0 && (
            <span className="ml-1 text-[var(--fop-primary)]">({pendingOps.length} pending)</span>
          )}
        </div>
        <button
          type="button"
          onClick={onCaptureClick}
          disabled={uploading || !sheetId}
          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-mono uppercase tracking-wider border border-[var(--fop-primary)] text-[var(--fop-primary)] hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition"
          data-testid="photo-capture-btn"
          title={originLabel ? `Capture photo or video for ${originLabel}` : "Capture photo or video"}
        >
          <Camera size={11} />
          {uploading ? "Saving…" : "Capture"}
        </button>
        {/* Native file input wired with `capture=environment` — on mobile
            this opens the rear camera directly; the OS-native capture
            sheet lets the user choose photo OR a short video (Wave 8,
            2026-09-11) without a second button. On desktop it falls
            back to a file picker for either type. Both flows POST the
            same endpoint — the server tells photo from video apart by
            magic bytes, not by this accept hint. */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,video/mp4,video/quicktime,video/webm"
          capture="environment"
          className="hidden"
          onChange={onFileChosen}
          data-testid="photo-capture-input"
        />
      </div>

      {totalCount === 0 ? (
        <div className="text-[10px] text-[var(--fop-text-3)] italic" data-testid="photo-timeline-empty">
          No photos yet — tap Capture.
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-1.5">
          {/* Pending (queued) captures render first — they're the newest
              local action. Tapping them does nothing destructive (a pending
              capture isn't a server doc yet). */}
          {pendingOps.map((op) => (
            <PendingThumb key={op.id} op={op} />
          ))}
          {photos.map((p) => (
            <PhotoThumb key={p.id} photo={p} onOpen={() => setLightbox(p)} />
          ))}
        </div>
      )}

      {lightbox && (
        <PhotoLightbox photo={lightbox} onClose={() => setLightbox(null)} onDelete={() => onDelete(lightbox.id)} />
      )}
    </div>
  );
}


/**
 * PendingThumb — Iter 36 optimistic UI. Renders a queued photo from its
 * local Blob via createObjectURL. Marked with a tiny "Pending" badge so
 * the field user knows it hasn't synced yet but trusts it's preserved.
 */
function PendingThumb({ op }) {
  const isVideo = (op.content_type || "").startsWith("video/");
  const url = useMemo(() => {
    try { return op.blob ? URL.createObjectURL(op.blob) : null; }
    catch { return null; }
  }, [op.blob]);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  return (
    <div
      className="relative aspect-[4/3] overflow-hidden rounded border border-[var(--fop-primary)] bg-[var(--fop-surface-2)]"
      data-testid={`photo-pending-${op.id}`}
      title="Queued — will sync when online"
    >
      {url && isVideo ? (
        <video src={url} className="w-full h-full object-cover opacity-90" muted />
      ) : url ? (
        <img src={url} alt="pending capture" className="w-full h-full object-cover opacity-90" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-[10px] font-mono text-[var(--fop-text-3)]">
          pending
        </div>
      )}
      <div className="absolute top-0 inset-x-0 px-1 py-0.5 bg-[var(--fop-primary)]/85 text-[8.5px] font-mono uppercase tracking-wider text-black text-center">
        Pending sync
      </div>
    </div>
  );
}


function PhotoThumb({ photo, onOpen }) {
  const date = useMemo(() => formatDateChip(photo.captured_at), [photo.captured_at]);
  const isVideo = photo.media_kind === "video";
  return (
    <button
      type="button"
      onClick={onOpen}
      className="relative aspect-[4/3] overflow-hidden rounded border border-[var(--fop-border)] hover:border-[var(--fop-primary)] transition group"
      data-testid={`photo-thumb-${photo.id}`}
      data-photo-id={photo.id}
      data-media-kind={photo.media_kind || "photo"}
      title={`${photo.origin_label || photo.origin_type} · ${date}`}
    >
      <img
        src={photoThumbUrl(photo.id)}
        alt={photo.caption || photo.origin_label || (isVideo ? "video" : "photo")}
        className="w-full h-full object-cover"
        loading="lazy"
      />
      {isVideo && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid="video-thumb-play-badge">
          <div className="w-7 h-7 rounded-full bg-black/60 flex items-center justify-center">
            <Play size={12} className="text-white fill-white" />
          </div>
        </div>
      )}
      <div className="absolute bottom-0 inset-x-0 px-1 py-0.5 bg-black/60 text-[8.5px] font-mono uppercase tracking-wider text-[var(--fop-text-2)] text-right">
        {isVideo && photo.duration_seconds != null ? `${formatDuration(photo.duration_seconds)} · ` : ""}{date}
      </div>
    </button>
  );
}


function PhotoLightbox({ photo, onClose, onDelete }) {
  // Esc closes; restrained shell so the photo/video dominates.
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  const isVideo = photo.media_kind === "video";

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="photo-lightbox"
      data-photo-id={photo.id}
      data-media-kind={photo.media_kind || "photo"}
    >
      <div
        className="relative max-w-[92vw] max-h-[92vh] flex flex-col items-center gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        {isVideo ? (
          <video
            src={photoFileUrl(photo.id)}
            controls
            autoPlay
            className="max-h-[80vh] max-w-[92vw] rounded shadow-2xl bg-black"
            data-testid="video-lightbox-player"
          />
        ) : (
          <img
            src={photoFileUrl(photo.id)}
            alt={photo.caption || photo.origin_label || "photo"}
            className="max-h-[80vh] max-w-[92vw] object-contain rounded shadow-2xl"
          />
        )}
        <div className="flex items-center justify-between gap-4 w-full px-2 py-1.5 bg-[var(--fop-surface)]/95 rounded border border-[var(--fop-border)]">
          <div className="text-[10px] font-mono text-[var(--fop-text-2)] min-w-0 truncate">
            <span className="text-[var(--fop-primary)]">{photo.origin_label || photo.origin_type}</span>
            <span className="text-[var(--fop-text-3)] mx-2">·</span>
            <span>{formatFullDate(photo.captured_at)}</span>
            <span className="text-[var(--fop-text-3)] mx-2">·</span>
            <span>{photo.captured_by_name}</span>
            {photo.gps_lat != null && photo.gps_lon != null && (
              <>
                <span className="text-[var(--fop-text-3)] mx-2">·</span>
                <span>{photo.gps_lat.toFixed(5)}, {photo.gps_lon.toFixed(5)}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={onDelete}
              className="p-1 text-[var(--fop-text-3)] hover:text-[var(--st-escalated)]"
              data-testid="photo-lightbox-delete"
              aria-label="Delete photo"
              title="Delete photo"
            >
              <Trash2 size={14} />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-1 text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
              data-testid="photo-lightbox-close"
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>
        </div>
        {photo.caption && (
          <div className="text-[11px] text-[var(--fop-text-2)] max-w-[640px] text-center leading-snug">
            {photo.caption}
          </div>
        )}
      </div>
    </div>
  );
}


function formatDateChip(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
}

function formatFullDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function formatDuration(seconds) {
  const s = Math.round(seconds);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}
